/**
 * Canonical Mastery Module — Single Source of Truth
 *
 * Family-first mastery system with group-scoped progress.
 * This is the ONLY module for mastery calculations.
 *
 * MeesterJay 4.1 Architecture:
 * - FamilyMastery is the authoritative mastery source
 * - Module/group completion derived from family mastery
 * - Question states are attempt history only (NOT mastery)
 * - All progress is group-scoped
 */

import {
  type ModuleId,
  getFamiliesForModule,
  getFamilyDefinition,
  type FamilyDefinition,
  getAllowedTiersForGroup,
} from '../content'
import { FamilyMasteryState } from './familyMasteryEngine'
import { GroupId } from '../config/systemConfig'
import { getFamilyMasteryKey } from './familyMasteryEngine'
import { MASTERY_LEVEL_THRESHOLDS } from '../config/constants'

// ============================================================
// INTERNAL HELPERS (DRY — extracted in R2 audit)
// ============================================================

/**
 * Get families for a module filtered to the group's allowed tier range.
 * Eliminates the 3-line pattern that was repeated 8× in this file.
 */
function getEligibleFamiliesForModule(
  moduleId: ModuleId,
  group: GroupId,
): FamilyDefinition[] {
  const moduleFamilies = getFamiliesForModule(moduleId)
  const allowedTiers = getAllowedTiersForGroup(group)
  return moduleFamilies.filter((f) => allowedTiers.has(f.tier))
}

/**
 * Resolve family definitions to their mastery states for a given group.
 * Returns only families that have been attempted (attempts > 0).
 * Eliminates the map+filter pattern that was repeated 6× in this file.
 */
function resolveFamilyMasteryStates(
  familyMastery: Record<string, FamilyMasteryState>,
  families: FamilyDefinition[],
  group: GroupId,
): FamilyMasteryState[] {
  return families
    .map((def) => {
      const key = getFamilyMasteryKey(def.familyId, group)
      return familyMastery[key]
    })
    .filter(
      (state): state is FamilyMasteryState =>
        state !== undefined && state.attempts > 0,
    )
}

// ============================================================
// FAMILY MASTERY QUERIES (Canonical - Group-Scoped)
// ============================================================

/**
 * Get the mastery score for a specific familyId in a specific group.
 *
 * ⚠️ **AMBIGUOUS RETURN — F-MAS-R1-INITIAL-SCORE-AMBIGUITY (mitigated 13 mei 2026):**
 * A return value of `0` does NOT distinguish between two cases:
 *   1. The family has **never been attempted** in this group (`attempts === 0`).
 *   2. The family has been attempted but the learner is **deeply struggling** (`masteryScore === 0`).
 *
 * For aggregations or UI that need to differentiate "unseen" vs "struggling",
 * use `hasFamilyAttempts()` or `getFamilyMasteryAttempts()` alongside this function.
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param familyId - The canonical familyId
 * @param group - The school group (6, 7, or 8)
 * @returns Mastery score (0-100); 0 if never attempted OR if deeply struggling
 */
export function getFamilyMasteryScore(
  familyMastery: Record<string, FamilyMasteryState>,
  familyId: string,
  group: GroupId,
): number {
  const key = getFamilyMasteryKey(familyId, group)
  const state = familyMastery[key]
  return state?.masteryScore ?? 0
}

/**
 * Get the attempt count for a specific familyId in a specific group.
 * Returns 0 if the family has never been attempted.
 *
 * Use this in combination with `getFamilyMasteryScore()` to disambiguate
 * "never attempted" (attempts === 0) from "struggling" (attempts > 0, score low).
 *
 * Part of F-MAS-R1-INITIAL-SCORE-AMBIGUITY mitigation (13 mei 2026).
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param familyId - The canonical familyId
 * @param group - The school group (6, 7, or 8)
 * @returns Attempt count (>= 0)
 */
export function getFamilyMasteryAttempts(
  familyMastery: Record<string, FamilyMasteryState>,
  familyId: string,
  group: GroupId,
): number {
  const key = getFamilyMasteryKey(familyId, group)
  return familyMastery[key]?.attempts ?? 0
}

/**
 * Check whether the learner has attempted a family at least once in a specific group.
 *
 * Use this as a guard before relying on `getFamilyMasteryScore()`:
 *   - `hasFamilyAttempts() === false` → score 0 means "unseen"
 *   - `hasFamilyAttempts() === true && score < BEGINNER` → struggling
 *
 * Part of F-MAS-R1-INITIAL-SCORE-AMBIGUITY mitigation (13 mei 2026).
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param familyId - The canonical familyId
 * @param group - The school group (6, 7, or 8)
 * @returns True if attempts > 0
 */
export function hasFamilyAttempts(
  familyMastery: Record<string, FamilyMasteryState>,
  familyId: string,
  group: GroupId,
): boolean {
  return getFamilyMasteryAttempts(familyMastery, familyId, group) > 0
}

/**
 * Check if a family is considered "mastered" in a specific group.
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param familyId - The canonical familyId
 * @param group - The school group (6, 7, or 8)
 * @returns True if mastered in this group
 */
export function isFamilyMastered(
  familyMastery: Record<string, FamilyMasteryState>,
  familyId: string,
  group: GroupId,
): boolean {
  return (
    getFamilyMasteryScore(familyMastery, familyId, group) >=
    MASTERY_LEVEL_THRESHOLDS.GEVORDERD
  )
}

/**
 * Get a human-readable mastery level label for a score.
 */
export function getFamilyMasteryLevel(
  score: number,
): 'onbekend' | 'beginner' | 'oefenaar' | 'gevorderd' | 'meester' {
  if (score <= 0) return 'onbekend'
  if (score < MASTERY_LEVEL_THRESHOLDS.BEGINNER) return 'beginner'
  if (score < MASTERY_LEVEL_THRESHOLDS.OEFENAAR) return 'oefenaar'
  if (score < MASTERY_LEVEL_THRESHOLDS.GEVORDERD) return 'gevorderd'
  return 'meester'
}

/**
 * Alias for getFamilyMasteryLevel — backward compatibility.
 * Consolidated from familyMasteryEngine.ts (D-SCAN D-2).
 */
export const getMasteryLevel = getFamilyMasteryLevel

/**
 * Get color class for mastery score (for UI display).
 */
export function getMasteryColor(score: number): string {
  if (score < MASTERY_LEVEL_THRESHOLDS.BEGINNER) return 'text-rose-600'
  if (score < MASTERY_LEVEL_THRESHOLDS.OEFENAAR) return 'text-amber-600'
  if (score < MASTERY_LEVEL_THRESHOLDS.GEVORDERD) return 'text-emerald-600'
  return 'text-blue-600'
}

/**
 * Get background color class for mastery score.
 */
export function getMasteryBgColor(score: number): string {
  if (score < MASTERY_LEVEL_THRESHOLDS.BEGINNER) return 'bg-rose-100'
  if (score < MASTERY_LEVEL_THRESHOLDS.OEFENAAR) return 'bg-amber-100'
  if (score < MASTERY_LEVEL_THRESHOLDS.GEVORDERD) return 'bg-emerald-100'
  return 'bg-blue-100'
}

// ============================================================
// MODULE MASTERY (Canonical - Group-Scoped)
// ============================================================

/**
 * Get module mastery from family mastery (CANONICAL).
 * This is the authoritative way to compute module mastery.
 *
 * ⚠️ **SEMANTICS — F-MAS-R1-MODULE-MASTERY-INFLATION (documented 13 mei 2026):**
 * This function computes the **average mastery score over ATTEMPTED families only**.
 * It answers the question: *"How well does the learner do on the parts they've touched?"*
 *
 * It does NOT answer: *"How well does the learner master this module overall?"* — because
 * unseen families do not penalise the score. A learner who has attempted 1 of 20 families
 * and scored 90 on it will see a "Module mastery = 90" reading, even though 19/20 families
 * are untouched.
 *
 * For overall module coverage / completion signals, use:
 *   - `getModuleCoverage()` for the attempted-vs-eligible ratio
 *   - `isModuleMasteredByFamilies()` for a strict "X% of families mastered" check
 *
 * Consumers should ideally combine BOTH signals in UI (score × coverage).
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param moduleId - Module to query
 * @param group - School group (REQUIRED for group-scoped mastery)
 * @returns Average mastery score over attempted families (0-100); 0 if none attempted
 */
export function getModuleMasteryFromFamilies(
  familyMastery: Record<string, FamilyMasteryState>,
  moduleId: ModuleId,
  group: GroupId,
): number {
  const eligible = getEligibleFamiliesForModule(moduleId, group)
  if (eligible.length === 0) return 0

  const attempted = resolveFamilyMasteryStates(familyMastery, eligible, group)
  if (attempted.length === 0) return 0

  const totalScore = attempted.reduce((sum, s) => sum + s.masteryScore, 0)
  return Math.round(totalScore / attempted.length)
}

/**
 * Get module coverage statistics for a learner in a specific group.
 *
 * Coverage answers: *"What fraction of the module's families has the learner touched?"*
 * It is the complement to `getModuleMasteryFromFamilies()` (depth of skill on touched
 * content) and addresses F-MAS-R1-MODULE-MASTERY-INFLATION by giving consumers an
 * explicit "breadth" signal alongside the existing "depth" signal.
 *
 * Recommended UI combination:
 *   - score (depth) × coverageRatio (breadth) = effective module mastery
 *
 * Added 13 mei 2026 as part of F-MAS-R1-MODULE-MASTERY-INFLATION mitigation (option B).
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param moduleId - Module to query
 * @param group - School group (REQUIRED)
 * @returns Object with attemptedCount, eligibleCount, coverageRatio (0-1)
 */
export function getModuleCoverage(
  familyMastery: Record<string, FamilyMasteryState>,
  moduleId: ModuleId,
  group: GroupId,
): { attemptedCount: number; eligibleCount: number; coverageRatio: number } {
  const eligible = getEligibleFamiliesForModule(moduleId, group)
  if (eligible.length === 0) {
    return { attemptedCount: 0, eligibleCount: 0, coverageRatio: 0 }
  }
  const attempted = resolveFamilyMasteryStates(familyMastery, eligible, group)
  return {
    attemptedCount: attempted.length,
    eligibleCount: eligible.length,
    coverageRatio: attempted.length / eligible.length,
  }
}

/**
 * Get module completion status from family mastery (CANONICAL).
 * A module is "mastered" when a threshold percentage of families are mastered.
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param moduleId - Module to query
 * @param group - School group (REQUIRED)
 * @param threshold - Percentage of families that must be mastered (default: 0.85 = 85%)
 * @returns True if module is mastered in this group
 */
export function isModuleMasteredByFamilies(
  familyMastery: Record<string, FamilyMasteryState>,
  moduleId: ModuleId,
  group: GroupId,
  threshold: number = 0.85,
): boolean {
  const eligible = getEligibleFamiliesForModule(moduleId, group)
  if (eligible.length === 0) return false

  const masteredCount = eligible.filter((def) => {
    const key = getFamilyMasteryKey(def.familyId, group)
    const state = familyMastery[key]
    return state && state.masteryScore >= MASTERY_LEVEL_THRESHOLDS.GEVORDERD
  }).length

  return masteredCount / eligible.length >= threshold
}

// ============================================================
// TIER-SPECIFIC MASTERY (Group-Scoped)
// ============================================================

/**
 * Get tier-specific mastery from family mastery.
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param moduleId - Module to query
 * @param tier - Tier to query (1-16)
 * @param group - School group (REQUIRED)
 * @returns Mastery percentage for this tier (0-100)
 */
export function getTierMasteryFromFamilies(
  familyMastery: Record<string, FamilyMasteryState>,
  moduleId: ModuleId,
  tier: number,
  group: GroupId,
): number {
  const eligible = getEligibleFamiliesForModule(moduleId, group).filter(
    (f) => f.tier === tier,
  )
  if (eligible.length === 0) return 0

  const attempted = resolveFamilyMasteryStates(familyMastery, eligible, group)
  if (attempted.length === 0) return 0

  const totalScore = attempted.reduce((sum, s) => sum + s.masteryScore, 0)
  return Math.round(totalScore / attempted.length)
}

/**
 * Get mastery by tier for all tiers in a module.
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param moduleId - Module to query
 * @param group - School group (REQUIRED)
 * @returns Record of tier → mastery percentage
 */
export function getAllTierMasteryFromFamilies(
  familyMastery: Record<string, FamilyMasteryState>,
  moduleId: ModuleId,
  group: GroupId,
): Record<number, number> {
  const eligible = getEligibleFamiliesForModule(moduleId, group)
  const tiers = [...new Set(eligible.map((f) => f.tier))]

  const result: Record<number, number> = {}
  tiers.forEach((tier) => {
    result[tier] = getTierMasteryFromFamilies(
      familyMastery,
      moduleId,
      tier,
      group,
    )
  })
  return result
}

// ============================================================
// FAMILY QUERIES (Group-Scoped)
// ============================================================

/**
 * Get struggling families for a module (mastery below BEGINNER threshold).
 * Useful for recommendations and focus areas.
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param moduleId - Module to query
 * @param group - School group (REQUIRED)
 * @returns Array of struggling family states, sorted by mastery (lowest first)
 */
export function getStrugglingFamiliesForModule(
  familyMastery: Record<string, FamilyMasteryState>,
  moduleId: ModuleId,
  group: GroupId,
): FamilyMasteryState[] {
  const eligible = getEligibleFamiliesForModule(moduleId, group)
  return resolveFamilyMasteryStates(familyMastery, eligible, group)
    .filter((state) => state.masteryScore < MASTERY_LEVEL_THRESHOLDS.BEGINNER)
    .sort((a, b) => a.masteryScore - b.masteryScore)
}

/**
 * Get mastered families for a module (mastery >= GEVORDERD threshold).
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param moduleId - Module to query
 * @param group - School group (REQUIRED)
 * @returns Array of mastered family states
 */
export function getMasteredFamiliesForModule(
  familyMastery: Record<string, FamilyMasteryState>,
  moduleId: ModuleId,
  group: GroupId,
): FamilyMasteryState[] {
  const eligible = getEligibleFamiliesForModule(moduleId, group)
  return eligible
    .map((def) => {
      const key = getFamilyMasteryKey(def.familyId, group)
      return familyMastery[key]
    })
    .filter(
      (state): state is FamilyMasteryState =>
        state !== undefined &&
        state.masteryScore >= MASTERY_LEVEL_THRESHOLDS.GEVORDERD,
    )
}

/**
 * Get weakest families for a module (for "Continue Learning" recommendations).
 * Returns families sorted by mastery score (lowest first), excluding unseen.
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param moduleId - Module to query
 * @param group - School group (REQUIRED)
 * @param limit - Maximum number of families to return (default: 3)
 * @returns Array of weakest family states
 */
export function getWeakestFamiliesForModule(
  familyMastery: Record<string, FamilyMasteryState>,
  moduleId: ModuleId,
  group: GroupId,
  limit: number = 3,
): FamilyMasteryState[] {
  const eligible = getEligibleFamiliesForModule(moduleId, group)
  return resolveFamilyMasteryStates(familyMastery, eligible, group)
    .sort((a, b) => a.masteryScore - b.masteryScore)
    .slice(0, limit)
}

/**
 * Get weakest families across ALL modules (for cross-module session recommendations).
 * Returns families sorted by mastery score (lowest first), excluding unseen.
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param group - School group (REQUIRED)
 * @param limit - Maximum number of families to return (default: 3)
 * @returns Array of weakest family states across all modules
 */
export function getWeakestFamiliesGlobal(
  familyMastery: Record<string, FamilyMasteryState>,
  group: GroupId,
  limit: number = 3,
): FamilyMasteryState[] {
  const allowedTiers = getAllowedTiersForGroup(group)
  const groupSuffix = `_g${group}`

  return Object.entries(familyMastery)
    .filter(([key, state]) => {
      if (!key.endsWith(groupSuffix)) return false
      if (!state || state.attempts === 0) return false
      const familyId = key.slice(0, -groupSuffix.length)
      const def = getFamilyDefinition(familyId)
      return def ? allowedTiers.has(def.tier) : false
    })
    .map(([_, state]) => state)
    .sort((a, b) => a.masteryScore - b.masteryScore)
    .slice(0, limit)
}

/**
 * Get strongest families across ALL modules in the current group (for Challenge mode).
 * Returns families sorted by mastery score (highest first), filtered to the group
 * via the `_g{group}` suffix on the mastery key. This is the mirror of
 * getWeakestFamiliesGlobal and is the canonical helper for Challenge-target selection.
 *
 * Resolves F-MAS-R3-CHALLENGE-MODE-CROSS-GROUP-LEAK: Object.entries(familyMastery)
 * without group-filter previously leaked mastery records from other groups.
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param group - School group (REQUIRED)
 * @param minScore - Minimum mastery score to qualify (default: 70 = OEFENAAR)
 * @param limit - Maximum number of families to return (default: 3)
 * @returns Array of strongest family states in this group
 */
export function getStrongestFamiliesGlobal(
  familyMastery: Record<string, FamilyMasteryState>,
  group: GroupId,
  minScore: number = MASTERY_LEVEL_THRESHOLDS.OEFENAAR,
  limit: number = 3,
): FamilyMasteryState[] {
  const allowedTiers = getAllowedTiersForGroup(group)
  const groupSuffix = `_g${group}`

  return Object.entries(familyMastery)
    .filter(([key, state]) => {
      if (!key.endsWith(groupSuffix)) return false
      if (!state || state.attempts === 0) return false
      if (state.masteryScore < minScore) return false
      const familyId = key.slice(0, -groupSuffix.length)
      const def = getFamilyDefinition(familyId)
      return def ? allowedTiers.has(def.tier) : false
    })
    .map(([_, state]) => state)
    .sort((a, b) => b.masteryScore - a.masteryScore)
    .slice(0, limit)
}

/**
 * Group-scoped weakest-family entry, with the original mastery key preserved
 * so consumers can perform additional lookups (decay status, attempt history,
 * adjacency joins) without re-deriving the `${familyId}_g${group}` key.
 *
 * Added 13 mei 2026 as part of F-MAS-R2-WEAKEST-GLOBAL-API-LOSES-KEY mitigation.
 */
export interface FamilyMasteryEntry {
  /** The group-scoped mastery key: `${familyId}_g${group}`. */
  key: string
  /** The mastery state for this family in this group. */
  state: FamilyMasteryState
}

/**
 * Get the weakest families across ALL modules in the current group, returning
 * BOTH the group-scoped key and the state.
 *
 * This is the entry-variant of `getWeakestFamiliesGlobal()`. It exists because
 * `FamilyMasteryState` has no `.key` field — consumers that want to do follow-up
 * lookups (e.g. `familyMastery[key]`, or join against per-question history) would
 * otherwise have to reconstruct the key from `state.familyId + group`, which is
 * easy to get wrong and produced a silent dead-path bug in SmartSessions before
 * the underlying API was patched.
 *
 * Resolves F-MAS-R2-WEAKEST-GLOBAL-API-LOSES-KEY (13 mei 2026). Prefer this
 * helper over the legacy `getWeakestFamiliesGlobal` whenever the consumer needs
 * the key or expects to do additional state lookups.
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param group - School group (REQUIRED)
 * @param limit - Maximum number of entries to return (default: 3)
 * @returns Array of `{ key, state }` entries sorted by mastery score (lowest first)
 */
export function getWeakestFamilyEntries(
  familyMastery: Record<string, FamilyMasteryState>,
  group: GroupId,
  limit: number = 3,
): FamilyMasteryEntry[] {
  const allowedTiers = getAllowedTiersForGroup(group)
  const groupSuffix = `_g${group}`

  return Object.entries(familyMastery)
    .filter(([key, state]) => {
      if (!key.endsWith(groupSuffix)) return false
      if (!state || state.attempts === 0) return false
      const familyId = key.slice(0, -groupSuffix.length)
      const def = getFamilyDefinition(familyId)
      return def ? allowedTiers.has(def.tier) : false
    })
    .map(([key, state]) => ({ key, state }))
    .sort((a, b) => a.state.masteryScore - b.state.masteryScore)
    .slice(0, limit)
}

/**
 * Get the strongest families across ALL modules in the current group, returning
 * BOTH the group-scoped key and the state.
 *
 * Symmetric counterpart to `getWeakestFamilyEntries`. Use this when Challenge-mode
 * or "show off your best" UIs need the group-scoped key alongside the state.
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param group - School group (REQUIRED)
 * @param minScore - Minimum mastery score to qualify (default: 70 = OEFENAAR)
 * @param limit - Maximum number of entries to return (default: 3)
 * @returns Array of `{ key, state }` entries sorted by mastery score (highest first)
 */
export function getStrongestFamilyEntries(
  familyMastery: Record<string, FamilyMasteryState>,
  group: GroupId,
  minScore: number = MASTERY_LEVEL_THRESHOLDS.OEFENAAR,
  limit: number = 3,
): FamilyMasteryEntry[] {
  const allowedTiers = getAllowedTiersForGroup(group)
  const groupSuffix = `_g${group}`

  return Object.entries(familyMastery)
    .filter(([key, state]) => {
      if (!key.endsWith(groupSuffix)) return false
      if (!state || state.attempts === 0) return false
      if (state.masteryScore < minScore) return false
      const familyId = key.slice(0, -groupSuffix.length)
      const def = getFamilyDefinition(familyId)
      return def ? allowedTiers.has(def.tier) : false
    })
    .map(([key, state]) => ({ key, state }))
    .sort((a, b) => b.state.masteryScore - a.state.masteryScore)
    .slice(0, limit)
}

/**
 * Get count of families by mastery level for a module.
 *
 * @param familyMastery - All family mastery states (group-scoped keys)
 * @param moduleId - Module to query
 * @param group - School group (REQUIRED)
 * @returns Counts by mastery level
 */
export function countFamiliesByMasteryLevel(
  familyMastery: Record<string, FamilyMasteryState>,
  moduleId: ModuleId,
  group: GroupId,
): {
  unseen: number
  beginner: number
  oefenaar: number
  gevorderd: number
  meester: number
} {
  const eligible = getEligibleFamiliesForModule(moduleId, group)
  const counts = {
    unseen: 0,
    beginner: 0,
    oefenaar: 0,
    gevorderd: 0,
    meester: 0,
  }
  eligible.forEach((def) => {
    const key = getFamilyMasteryKey(def.familyId, group)
    const state = familyMastery[key]
    if (!state || state.attempts === 0) {
      counts.unseen++
    } else if (state.masteryScore < MASTERY_LEVEL_THRESHOLDS.BEGINNER) {
      counts.beginner++
    } else if (state.masteryScore < MASTERY_LEVEL_THRESHOLDS.OEFENAAR) {
      counts.oefenaar++
    } else if (state.masteryScore < MASTERY_LEVEL_THRESHOLDS.GEVORDERD) {
      counts.gevorderd++
    } else {
      counts.meester++
    }
  })
  return counts
}
